import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AgCharts } from "ag-charts-react";

import { vacancyQuery, vacationBalancesGetAll } from "../../api";
import { customChartThemes } from "../../common/grid";
import { LoadingContainer } from "../../common/loaders/loadingcontainer";
import { localized } from "../../common/utils";
import { activeIntegrationsSelector } from "../../reducers";
import { useSelector } from "react-redux";

export function VacationDays({ cardProps, changeProperty }) {
  const { t } = useTranslation();

  const dataSource = cardProps?.dataSource ?? "payroll";
  const selectedYear = cardProps?.year;

  const { data: payrollData } = useQuery({ ...vacancyQuery });

  const { data: hrData } = useQuery({
    queryKey: ["hrVacancy"],
    queryFn: vacationBalancesGetAll,
    enabled: dataSource === "hr",
  });

  const [rawData, setRawData] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);

  const { chartTheme } = customChartThemes;

  const activeIntegrations = useSelector(activeIntegrationsSelector);
  const isProcountorActive = !!activeIntegrations.tokenProcountor;

  useEffect(() => {
    if (dataSource !== "payroll") return;
    if (!payrollData?.vacancy_response?.lomarp) return;

    const cleaned = payrollData.vacancy_response.lomarp
      .filter(item => item.pid > 0 || item.jalj > 0)
      .map(item => ({
        personName: item.nimi,
        vacationsUsed: Math.max(0, item.pid),
        vacationsRemaining: Math.max(0, item.jalj),
        year: String(item.vuosi),
      }));

    const years = [...new Set(cleaned.map(x => x.year))]
      .sort()
      .reverse();

    setRawData(cleaned);
    setAvailableYears(years);

    if (!selectedYear && years.length > 0) {
      changeProperty("year", years[0]);
    }
  }, [payrollData, dataSource]);

  useEffect(() => {
    if (dataSource !== "hr") return;
    if (!Array.isArray(hrData?.data)) return;

    const mapped = hrData.data
      .map(person => {
        const allowance = Number(person.vacationAllowanceDays ?? 0);
        const used = Number(person.usedVacationDays ?? 0);

        return {
          personName: person.name,
          vacationsUsed: used,
          vacationsRemaining: Math.max(0, allowance - used),
          year: person.annualVacationYear,
        };
      })
      .filter(p =>
        p.personName &&
        (p.vacationsUsed > 0 || p.vacationsRemaining > 0)
      );

    const years = [...new Set(mapped.map(x => x.year))]
      .sort()
      .reverse();

    setRawData(mapped);
    setAvailableYears(years);

    if (!selectedYear && years.length > 0) {
      changeProperty("year", years[0]);
    }
  }, [hrData, dataSource]);

  const formattedData = useMemo(() => {
    if (!selectedYear) return [];
    return rawData.filter(item => item.year === selectedYear);
  }, [rawData, selectedYear]);

  const series = [
    {
      type: "bar",
      xKey: "personName",
      direction: "horizontal",
      yKey: "vacationsUsed",
      yName: t("Used", "Käytetyt"),
      stacked: true,
    },
    {
      type: "bar",
      xKey: "personName",
      direction: "horizontal",
      yKey: "vacationsRemaining",
      yName: t("Remaining", "Jäljellä"),
      stacked: true,
    },
  ];

  if (!formattedData) return <LoadingContainer />;

  const rowHeight = 20;
  const chartHeight = formattedData.length * rowHeight + 100; // add space for axes and legend
  
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h4 style={{ color: "var(--heeros-blue)" }}>
          {t("VacationDays", "Lomapäivät")}

          <select
            style={{ marginLeft: ".5em", fontSize: "small" }}
            value={dataSource}
            onChange={(e) => {
              changeProperty("dataSource", e.target.value);
              changeProperty("year", undefined);
            }}
          >
            <option value="payroll">
              {t("InPayroll", "Palkanlaskennassa")}
            </option>
            <option value="hr">
              {t("InHRSystem", "HR-ohjelmassa")}
            </option>
          </select>
        </h4>

        {availableYears?.length > 0 && (
          <select
          style={{ marginRight: ".5em", fontSize: "small" }}
          value={selectedYear || ""}
            onChange={(e) => changeProperty("year", e.target.value)}
          >
            {availableYears.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        )}
      </div>

      <div style={{ maxHeight: "500px", overflowY: "auto" }}>
        <AgCharts
          options={{
            data: formattedData,
            height: chartHeight,
            series,
            axes: {
              x: { type: "number", },
              y: { type: "category", },
            },
            legend: {
              enabled: true,
              position: "top",
            },
            theme: {
              ...chartTheme,
              palette: {
                ...chartTheme.palette,
                fills: ["#8FE3D0", "#6F42C1"],
              },
            },
            locale: localized.agChartsLocale,
          }}
        />
      </div>
    </>
  );
}